import { requestGet } from "../../utils/request.ts";
import { AbstractTask, TaskType } from "./task.ts";
import { getDirectoryHandler, getFileHandler, removeEntry } from "../../utils/opfs_common.ts";
import { OPFSFile } from "../../utils/opfs_utils.ts";
import { CacheKV } from "../../utils/cache_kv.ts";
import { $username } from "../user_info.ts";

export interface PhotoInfo {
    photo_id: number;
    photo_name: string;
    photo_time: number;
    analysis?: Record<string, any>;
}

interface PhotoImage {
    photo_id: number;
    photo_url: string;
}

interface GetPhotoResponse {
    total: number;
    photos: PhotoInfo[];
}

interface GetPhotoImageResponse {
    photos: PhotoImage[];
}

const PHOTO_CACHE_NAME = "photo_cache_v2";

export const getAllPhotos = async (
    cameraId: number,
    beforeTime: number | undefined = undefined,
    afterTime: number | undefined = undefined,
    limit: number = 500,
) => {
    const photos: PhotoInfo[] = [];
    let total = 0;
    do {
        const resp = await requestGet<GetPhotoResponse>("/v2/photo", {
            camera_id: cameraId,
            before: Math.floor(beforeTime ?? (Date.now() / 1000)),
            after: Math.floor(afterTime ?? 0),
            limit: limit,
            reverse: 0,
        });
        total = resp.total;
        photos.push(...resp.photos);
        if (photos.length > 0) {
            beforeTime = photos.at(-1)!.photo_time - 1;
        }
        if (total <= 0 || total === resp.photos.length) {
            break;
        }
    } while (true);
    return photos;
};

export const getPhotos = async (
    cameraId: number,
    beforeTime: number | undefined = undefined,
    afterTime: number | undefined = undefined,
    limit: number = 500,
    reverse: boolean = false,
) => {
    const resp = await requestGet<GetPhotoResponse>("/v2/photo", {
        camera_id: cameraId,
        before: Math.floor(beforeTime ?? (Date.now() / 1000)),
        after: Math.floor(afterTime ?? 0),
        limit: limit,
        reverse: reverse ? 1 : 0,
    });
    return resp.photos;
};

export const getPhotosWithTotal = async (
    cameraId: number,
    beforeTime: number | undefined = undefined,
    afterTime: number | undefined = undefined,
    limit: number = 500,
    reverse: boolean = false,
) => {
    const resp = await requestGet<GetPhotoResponse>("/v2/photo", {
        camera_id: cameraId,
        before: Math.floor(beforeTime ?? (Date.now() / 1000)),
        after: Math.floor(afterTime ?? 0),
        limit: limit,
        reverse: reverse ? 1 : 0,
    });
    return resp;
};

export const clearVideoPhotoCache = async (photoId: number | undefined = undefined) => {
    if (photoId !== undefined) {
        const name = photoId.toString() + DownloadVideoPhotosTask.OPFS_PREFIX;
        const handler = await getFileHandler([...DownloadVideoPhotosTask.OPFS_PREFIX, name], false, false);
        await removeEntry(handler);
    } else {
        try {
            const handler = await getDirectoryHandler(DownloadVideoPhotosTask.OPFS_PREFIX, false);
            await removeEntry(handler);
        } catch (_) {
            // ignore
        }
    }
};

export const getPhotoImageURL = async (
    photoId: number,
    mode: "thumbnail" | "timeline" | "video" | "view" | "4k" | "download" = "thumbnail",
): Promise<string> => {
    // fetch photo url
    const resp1 = await requestGet<GetPhotoImageResponse>("/v2/photo_img", {
        photo_id: photoId.toString(),
        mode: mode ?? "thumbnail",
    });
    const url = resp1.photos[0].photo_url;
    return url;
};

export const getPhotoImageData = async (
    photoId: number,
    mode: "thumbnail" | "timeline" | "video" | "view" | "4k" | "download" = "thumbnail",
    signal?: AbortSignal,
): Promise<ArrayBuffer> => {
    // try cache
    const cacheKey = `${$username.get()}|${mode}|${photoId.toString()}`;
    const kv = await CacheKV.open(PHOTO_CACHE_NAME);
    if (await kv.has(cacheKey)) {
        const buffer = await kv.get(cacheKey) ?? new ArrayBuffer(0);
        return buffer;
    }
    // fetch photo
    const resp1 = await requestGet<GetPhotoImageResponse>("/v2/photo_img", {
        photo_id: photoId.toString(),
        mode: mode ?? "thumbnail",
    });
    const url = resp1.photos[0].photo_url;
    const resp2 = await fetch(url, {
        mode: "cors",
        signal,
    });
    if (!resp2.ok) {
        throw new Error(`Fetch image failed: ${resp2.status} ${resp2.statusText}`);
    }
    const data = await resp2.arrayBuffer();
    // cache photo
    await kv.set(cacheKey, data);
    return data;
};

export class DownloadVideoPhotosTask extends AbstractTask {
    static OPFS_PREFIX = ["CachedPhotos"];
    static IMG_POSTFIX = ".webp";
    #photos: PhotoInfo[];
    #files: File[];
    #processNum = 0;
    #processed = 0;
    constructor(photos: PhotoInfo[]) {
        super(TaskType.Download, photos.length);
        this.#photos = [...photos];
        this.#files = new Array(photos.length);
    }
    async #getImageFile(photoId: number, cacheDir: FileSystemDirectoryHandle, abortSignal: AbortSignal) {
        const name = photoId.toString() + DownloadVideoPhotosTask.IMG_POSTFIX;
        let photoFile: File;
        try {
            photoFile = await (await cacheDir.getFileHandle(name, { create: false })).getFile();
        } catch (_) {
            // not found, download and cache.
            // get download url
            const resp1 = await requestGet<GetPhotoImageResponse>("/v2/photo_img", {
                photo_id: photoId.toString(),
                mode: "video",
            });
            const url = resp1.photos[0].photo_url;
            // download image
            const resp2 = await fetch(url, {
                mode: "cors",
                signal: abortSignal,
            });
            const data = await resp2.arrayBuffer();
            const handler = await cacheDir.getFileHandle(name, { create: true });
            const opfsFile = new OPFSFile(handler);
            try {
                await opfsFile.open();
                await opfsFile.write(data);
            } finally {
                await opfsFile.close();
            }
            photoFile = await handler.getFile();
        }
        return photoFile;
    }
    async #downloadWorker(cacheDir: FileSystemDirectoryHandle, abortSignal: AbortSignal) {
        while (this.#processNum < this.#photos.length) {
            const idx = this.#processNum;
            this.#processNum += 1;
            
            // Try/catch for individual file download
            try {
                const photoFile = await this.#getImageFile(this.#photos[idx].photo_id, cacheDir, abortSignal);
                this.#files[idx] = photoFile;
                this.#processed += 1;
                this.$store.setKey("current", this.#processed);
            } catch (err) {
                console.warn(`Download failed for photo ${this.#photos[idx].photo_id}`, err);
                // Don't increment #processed so retry logic can work or we can handle partial failure
                // Actually, if we throw here, the Promise.allSettled will catch it.
                // But we need to reset #processNum logic carefully if we want to retry specific files.
                // For simplicity in this "do-while" retry loop: 
                // If this worker fails, it stops. The main loop checks processed vs processNum.
                throw err;
            }
        }
    }
    override _action = async (abortSignal: AbortSignal) => {
        const cacheDir = await getDirectoryHandler(DownloadVideoPhotosTask.OPFS_PREFIX, true);
        this.$store.setKey("current", 0);
        let retryCount = 0;
        
        // Mobile optimization: Limit concurrency to avoid memory issues
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        const concurrency = isMobile ? 3 : 6;

        do {
            // Check if we are done before starting workers (for retry logic)
            if (this.#processed >= this.#photos.length) break;

            // Reset processNum to processed count to pick up where we left off (or start from 0)
            // Actually, the previous logic reset both to 0 on retry. 
            // Better logic: Keep downloaded files, only retry missing ones.
            
            // Re-scan files array to find missing ones? 
            // The current implementation uses #processNum as a shared atomic counter.
            // If we retry, we reset everything. Let's keep it simple but reliable.
            
            if (retryCount > 0) {
                 // On retry, reset counters to retry all failed ones? 
                 // Or better: check which indices in #files are empty.
                 // But #processNum is a simple index.
                 // Let's stick to original retry logic but limit concurrency for mobile.
                 this.#processNum = 0;
                 this.#processed = 0;
            }

            const workers = [];
            for (let i = 0; i < concurrency; i++) {
                workers.push(this.#downloadWorker(cacheDir, abortSignal));
            }

            await Promise.allSettled(workers);
            
            if (this.#processed === this.#photos.length) {
                break;
            }
            
            // If failed, wait and retry
            console.log(`Download retry ${retryCount + 1}... Processed: ${this.#processed}/${this.#photos.length}`);
            await new Promise((rs) => setTimeout(rs, 1000));
            
            retryCount += 1;
            if (retryCount >= 3) {
                throw new Error("Download Photo Failed! Please check network.");
            }
        } while (true);
        abortSignal.throwIfAborted();
    };
    getDownloadedFiles() {
        return [...this.#files];
    }
}
